import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import TextButton from 'LuxKit/Button/TextButton'
import ClassicRewardsSealIcon from 'LuxKit/Icons/loyalty/ClassicRewardsSealIcon'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import useOpenCharterFareClassModal from 'hooks/OfferPage/useOpenCharterFareClassModal'
import type { FareClassWithReward } from 'constants/flight'

const Card = styled(Pane)`
  border-radius: ${(props) => props.theme.borderRadius.S};
  padding: ${rem(16)};
  text-align: center;
`

interface OwnProps {
  rewardPointsDeficit: number
}

interface MappedProps {
  offer?: App.Offer
  fareClass: FareClassWithReward
  checkInDate?: string
  checkOutDate?: string
  occupancies: Array<App.Occupants>
}

type Props = OwnProps & MappedProps

/**
 * Card shown when the member has a rewards fare
 * selected but not enough loyalty/societe points to redeem it.
 */
function FlashHotelInsufficientLoyaltyPointsCard({ rewardPointsDeficit, offer, fareClass, checkInDate, checkOutDate, occupancies }: Props) {
  const intl = useIntl()
  const loyaltyProgramName = getLoyaltyProgramDisplayName()
  const { canOpen, open } = useOpenCharterFareClassModal({ offer, fareClass, checkInDate, checkOutDate, occupancies })

  return (
    <Card background="light-background">
      <Group direction="vertical" gap={8}>
        <Group direction="horizontal" verticalAlign="center" horizontalAlign="center" gap={4}>
          <ClassicRewardsSealIcon size={20} colour="highlight-primary-dark" aria-hidden />
          <BodyText variant="medium" weight="bold" colour="highlight-primary-dark" as="span">
            {intl.formatMessage(
              {
                id: 'HK_aIz5NHfsk81_smQ0n',
                defaultMessage: '{loyaltyProgramName} Rewards',
                description: 'Heading of the insufficient-points card on the Flash hotel offer page; the configured loyalty program name (e.g. Société) followed by "Rewards".',
              },
              { loyaltyProgramName },
            )}
          </BodyText>
        </Group>
        <BodyText variant="small" colour="neutral-one">
          <FormattedMessage
            id="T-O-nWnp1KqxvP4l28Xe"
            defaultMessage="You require an additional {points} to book this package."
            description="Message shown on offer page package options when the user does not have enough loyalty points to book with a reward fare class."
            values={{ points: <FormatLoyaltyPoints points={rewardPointsDeficit} /> }}
          />
        </BodyText>
        <TextButton kind="primary" fit="full-width" to="/societe/buy-points" size="small" target="_blank">
          {intl.formatMessage(
            {
              id: 'JlfdbK8ELsMZtYtkH-mb',
              defaultMessage: 'Buy {loyaltyProgramName} Points',
              description: 'Primary CTA on the insufficient-points card on the Flash hotel offer page; navigates to the loyalty buy-points page.',
            },
            { loyaltyProgramName },
          )}
        </TextButton>
        {canOpen && (
          <>
            <Caption variant="medium" colour="neutral-two">
              {intl.formatMessage({
                id: 'Chrk1O53GPR6pKXkK7j1',
                defaultMessage: 'or',
                description: 'Separator between the primary and secondary CTAs on the insufficient-points card on the Flash hotel offer page.',
              })}
            </Caption>
            <TextButton kind="secondary" fit="full-width" onClick={open} size="small">
              {intl.formatMessage({
                id: '2LeFYRhZ7kg-G7Zqi5ME',
                defaultMessage: 'View other options',
                description: 'Secondary CTA on the insufficient-points card on the Flash hotel offer page; opens the fare-class selection modal so members can pick a non-reward fare.',
              })}
            </TextButton>
          </>
        )}
      </Group>
    </Card>
  )
}

export default withOfferPageState((state): MappedProps => ({
  offer: state.offer,
  fareClass: state.fareClass,
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
  occupancies: state.rooms,
}), FlashHotelInsufficientLoyaltyPointsCard)
